import { useState, useEffect, useCallback } from 'react';
import { doc, onSnapshot, updateDoc, Timestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db } from '../firebase';
import { functions } from '../lib/firebase';
import { useAuth } from '../context/AuthContext';

export interface TrailDropOff {
  loanId: string;
  brokerName: string;
  lender: string;
  prevMonthCents: number;
  annualRiskCents: number;
}

export interface MonthlyDataPoint {
  month: string;
  upfrontCents: number;
  trailCents: number;
  totalCents: number;
  lotCount: number;
}

export interface BriefingData {
  generatedAt: Date;
  comparisonWindow: { startMonth: string; endMonth: string };
  executiveSummary: string;
  trailDropOffs: TrailDropOff[];
  droppedCount: number;
  newCount: number;
  netChange: number;
  keyInsights: string[];
  bookHealthScore: number;
  bookHealthBucket: 'critical' | 'needs_attention' | 'healthy' | 'strong';
  bookHealthNarrative: string;
  portfolioStats: {
    totalLoans: number;
    lineOfTrail: number;
    monthlyTrailCents: number;
    monthlyUpfrontCents: number;
    totalCommissionCents: number;
    totalTrailCents: number;
    totalUpfrontCents: number;
    bookValuationCents: number;
  };
  monthlyTrend: MonthlyDataPoint[];
  dismissed: boolean;
}

interface UseFirstLoginBriefingReturn {
  briefing: BriefingData | null;
  loading: boolean;
  error: string | null;
  dismiss: () => Promise<void>;
  generate: () => Promise<void>;
  generating: boolean;
}

export function useFirstLoginBriefing(companyId: string | undefined): UseFirstLoginBriefingReturn {
  const [briefing, setBriefing] = useState<BriefingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const { user } = useAuth();

  // Real-time listener on the briefing document
  useEffect(() => {
    if (!companyId) {
      setLoading(false);
      return;
    }

    const briefingRef = doc(db, 'companies', companyId, 'ai_briefings', 'first_login');

    const unsubscribe = onSnapshot(
      briefingRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          setBriefing({
            generatedAt: data.generatedAt?.toDate?.() || new Date(),
            comparisonWindow: data.comparisonWindow || { startMonth: '', endMonth: '' },
            executiveSummary: data.executiveSummary || '',
            trailDropOffs: data.trailDropOffs || [],
            droppedCount: data.droppedCount || 0,
            newCount: data.newCount || 0,
            netChange: data.netChange || 0,
            keyInsights: data.keyInsights || [],
            bookHealthScore: data.bookHealthScore || 0,
            bookHealthBucket: data.bookHealthBucket || 'needs_attention',
            bookHealthNarrative: data.bookHealthNarrative || '',
            portfolioStats: {
              totalLoans: 0,
              lineOfTrail: 0,
              monthlyTrailCents: 0,
              monthlyUpfrontCents: 0,
              totalCommissionCents: 0,
              totalTrailCents: 0,
              totalUpfrontCents: 0,
              bookValuationCents: 0,
              ...data.portfolioStats,
            },
            monthlyTrend: data.monthlyTrend || [],
            dismissed: data.dismissed || false,
          });
        } else {
          setBriefing(null);
        }
        setLoading(false);
      },
      (err) => {
        console.error('[useFirstLoginBriefing] Listener error:', err);
        setError('Failed to load briefing');
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [companyId]);

  const dismiss = useCallback(async () => {
    if (!companyId || !user) return;
    const briefingRef = doc(db, 'companies', companyId, 'ai_briefings', 'first_login');
    await updateDoc(briefingRef, {
      dismissed: true,
      dismissedAt: Timestamp.now(),
      dismissedBy: user.uid,
    });
  }, [companyId, user]);

  const generate = useCallback(async () => {
    if (!companyId) return;
    setGenerating(true);
    setError(null);
    try {
      const generateFn = httpsCallable<
        { companyId: string },
        { success: boolean; comparisonWindow?: { startMonth: string; endMonth: string } }
      >(functions, 'generateFirstLoginBriefing', { timeout: 180000 });
      await generateFn({ companyId });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to generate briefing';
      setError(message);
      console.error('[useFirstLoginBriefing] Generate error:', err);
    } finally {
      setGenerating(false);
    }
  }, [companyId]);

  return { briefing, loading, error, dismiss, generate, generating };
}
